import { useState, useEffect } from "react";
import { Share2, Copy, CheckCircle2, Search, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Reusable ShareDialog for sharing courses, assessments, compliance templates, and reports.
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - itemType: "course" | "assessment" | "compliance" | "report"
 * - itemId: string
 * - itemTitle: string
 */
export const ShareDialog = ({ open, onOpenChange, itemType, itemId, itemTitle }) => {
  const { user } = useAuth();
  const [teamUsers, setTeamUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedUsers([]);
      setMessage("");
      setSearch("");
      fetchUsers();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users");
      setTeamUsers((res.data || []).filter(u => u.id !== user?.id));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const toggleUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSubmit = async () => {
    if (selectedUsers.length === 0) return;
    setSubmitting(true);
    try {
      await api.post(`/share/${itemType}/${itemId}`, {
        recipient_ids: selectedUsers,
        message,
      });
      toast.success(`Shared with ${selectedUsers.length} team member${selectedUsers.length > 1 ? "s" : ""}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to share");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    const typePathMap = {
      course: `/course/${itemId}`,
      assessment: `/assessments/${itemId}`,
      compliance: `/compliance/${itemId}`,
      report: `/reports`,
    };
    navigator.clipboard.writeText(`${window.location.origin}${typePathMap[itemType] || "/"}`);
    toast.success("Link copied to clipboard");
  };

  const typeLabel = {
    course: "Course",
    assessment: "Assessment",
    compliance: "Compliance Template",
    report: "Report",
  }[itemType] || "Item";

  const filteredUsers = teamUsers.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.full_name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || (u.department || "").toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" /> Share {typeLabel}
          </DialogTitle>
          <DialogDescription className="line-clamp-2">
            Share "{itemTitle}" with team members
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={copyLink} data-testid="share-copy-link-btn">
            <Copy className="w-3.5 h-3.5" /> Copy Link
          </Button>

          <div>
            <Label className="text-xs">Select recipients</Label>
            <div className="relative mt-1 mb-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email, department..."
                className="pl-8 h-8 text-xs"
                data-testid="share-search-input"
              />
            </div>
            <div className="max-h-[200px] overflow-y-auto space-y-0.5 border rounded-lg p-1.5">
              {loading ? (
                <div className="flex items-center justify-center py-6 text-muted-foreground text-xs">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground text-xs">No users found</p>
              ) : (
                filteredUsers.map(u => (
                  <div
                    key={u.id}
                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                      selectedUsers.includes(u.id) ? "bg-primary/10" : "hover:bg-muted/50"
                    }`}
                    onClick={() => toggleUser(u.id)}
                    data-testid={`share-user-${u.id}`}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                      selectedUsers.includes(u.id) ? "bg-primary border-primary" : "border-muted-foreground/30"
                    }`}>
                      {selectedUsers.includes(u.id) && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium truncate block">{u.full_name}</span>
                      <span className="text-[10px] text-muted-foreground">{u.email}</span>
                    </div>
                    {u.department && (
                      <Badge variant="outline" className="text-[9px] shrink-0">{u.department}</Badge>
                    )}
                  </div>
                ))
              )}
            </div>
            {selectedUsers.length > 0 && (
              <p className="text-xs text-primary mt-1">{selectedUsers.length} selected</p>
            )}
          </div>

          <div>
            <Label className="text-xs">Message (optional)</Label>
            <Input
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={`Check out this ${typeLabel.toLowerCase()}!`}
              className="mt-1"
              data-testid="share-message-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedUsers.length === 0 || submitting}
            data-testid="share-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Share ({selectedUsers.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
